import { el } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { button } from '../../components/button.js';
import {
  PRESETS, SIZES, WIDGETS, groupsFor, layoutFromPreset, sortedWidgets,
  spanFor, widgetMeta, cycleSize, appendV2Widgets, SECTION_IDS,
} from './layout.js';

export function isPhone() {
  return window.matchMedia('(max-width: 767px)').matches;
}

/**
 * Restore a saved layout. Unknown widgets are dropped; new S45 section
 * widgets are appended once so existing arrangements survive (§6a).
 * @param {unknown} value
 * @param {{ available?: string[], migrate?: boolean }} [opts]
 */
export function layoutFromSaved(value, opts = {}) {
  if (!value || typeof value !== 'object' || !PRESETS[value.preset] || !Array.isArray(value.widgets)) {
    return layoutFromPreset('overview');
  }
  const seen = new Set();
  const widgets = [];
  sortedWidgets(value.widgets).forEach((widget) => {
    if (!widget || seen.has(widget.id)) return;
    if (!WIDGETS.some((item) => item.id === widget.id)) return;
    if (!SIZES.includes(widget.size)) return;
    seen.add(widget.id);
    widgets.push({ id: widget.id, size: widget.size, order: widgets.length });
  });
  const base = { preset: value.preset, widgets };
  if (opts.migrate === false) return base;
  return appendV2Widgets(base, { available: opts.available });
}

export function saveLayout(productId, layout) {
  const query = productId ? `?product_id=${encodeURIComponent(productId)}` : '';
  return api(`/me/ui-prefs/dashboard_layout${query}`, {
    method: 'PUT',
    body: { value: layout },
  });
}

export function renderEditBar(actions) {
  return el('div', { class: 'dash-editbar', role: 'region', 'aria-label': 'Layout editor' },
    el('span', { class: 'dash-edit-dot', 'aria-hidden': 'true' }),
    el('span', { class: 'dash-kicker' }, 'Editing layout'),
    el('span', { class: 'dash-edit-hint' }, 'Drag a card to move it. Use ⋮⋮ with arrow keys, S–XL to resize, × to remove.'),
    button('Add widgets', { onClick: () => actions.openModal('widgets') }),
    button('Layouts', { onClick: () => actions.openModal('layout') }),
    button('Reset', { onClick: actions.reset }),
    button('Done', { variant: 'primary', onClick: actions.done }));
}

export function renderPhonePresets(layout, actions) {
  return el('div', { class: 'dash-phone-presets', role: 'group', 'aria-label': 'Layout presets' },
    Object.keys(PRESETS).map((id) => button(PRESETS[id].name, {
      className: layout.preset === id ? 'is-on' : '',
      onClick: () => actions.pickPreset(id),
    })));
}

function miniCells(presetId) {
  const layout = layoutFromPreset(presetId);
  return sortedWidgets(layout.widgets).map((widget) => el('span', {
    class: widget.id === 'insights' || widget.id.startsWith('m') ? '' : '',
    style: `grid-column: span ${spanFor(widget, presetId)}; height: 10px`,
  }));
}

export function renderModal(state, actions) {
  const tab = state.modal;
  const draft = state.draft;
  const groups = groupsFor(draft.widgets);
  return el('div', { class: 'dash-modal-root' },
    el('button', { type: 'button', class: 'dash-scrim', 'aria-label': 'Close layout', onClick: actions.closeModal }),
    el('div', { class: 'dash-modal', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'dash-layout-title' },
      el('div', { class: 'dash-modal-head' },
        el('div', {},
          el('p', { class: 'dash-kicker' }, 'Dashboard'),
          el('h2', { id: 'dash-layout-title', class: 'dash-serif' }, 'Customise ', el('em', {}, 'layout.'))),
        button('Close', { onClick: actions.closeModal, 'aria-label': 'Close' })),
      el('div', { class: 'dash-tabs', role: 'tablist' },
        el('button', {
          type: 'button', role: 'tab', class: 'dash-tab', 'aria-selected': tab === 'layout' ? 'true' : 'false',
          onClick: () => actions.setTab('layout'),
        }, 'Layout'),
        el('button', {
          type: 'button', role: 'tab', class: 'dash-tab', 'aria-selected': tab === 'widgets' ? 'true' : 'false',
          onClick: () => actions.setTab('widgets'),
        }, `Widgets ${draft.widgets.length} / ${WIDGETS.length}`)),
      el('div', { class: 'dash-modal-body' },
        tab === 'widgets' ? widgetList(groups, draft, actions) : presetList(draft, actions),
        el('div', { class: 'dash-preview' },
          el('p', { class: 'dash-kicker' }, el('span', {}, 'Output preview'), el('span', {}, PRESETS[draft.preset].name)),
          el('div', { class: 'dash-preview-grid' }, sortedWidgets(draft.widgets).map((widget) => el('div', {
            style: `grid-column: span ${spanFor(widget, draft.preset)}`,
          }, el('span', {}, widget.size), el('span', {}, widgetMeta(widget.id).title)))),
          el('p', { class: 'dash-note' }, 'On tablets, small cards pair up two per row; on phones every card goes full width.'))),
      el('div', { class: 'dash-modal-foot' },
        el('span', { class: 'dash-note' }, `${PRESETS[draft.preset].name} · ${draft.widgets.length} of ${WIDGETS.length} widgets`),
        button('Cancel', { onClick: actions.closeModal }),
        button('Apply & arrange', { onClick: () => actions.apply(true) }),
        button('Apply layout', { variant: 'primary', onClick: () => actions.apply(false) }))));
}

function presetList(draft, actions) {
  return el('div', { class: 'dash-presets' }, Object.keys(PRESETS).map((id) => {
    const preset = PRESETS[id];
    return el('button', {
      type: 'button',
      class: 'dash-preset',
      'aria-pressed': draft.preset === id ? 'true' : 'false',
      onClick: () => actions.pickDraft(id),
    },
    el('span', { class: 'dash-mini', 'aria-hidden': 'true' }, miniCells(id)),
    el('span', { class: 'dash-preset-name' }, preset.name),
    el('span', { class: 'dash-copy' }, preset.desc));
  }));
}

function widgetList(groups, draft, actions) {
  const on = new Set(draft.widgets.map((widget) => widget.id));
  const hidden = new Map((actions.hiddenSections || []).map((h) => [h.id, h]));
  return el('div', { class: 'dash-widget-groups' }, groups.map((group) => el('section', {},
    el('p', { class: 'dash-kicker' }, el('span', {}, group.name), el('span', {}, `${group.on} / ${group.total} on board`)),
    group.items.map((item) => {
      const added = on.has(item.id);
      const reason = SECTION_IDS.includes(item.id) ? hidden.get(item.id) : null;
      const disabled = !!(reason && !added);
      return el('div', { class: disabled ? 'dash-wrow is-disabled' : 'dash-wrow' },
        el('span', {}, item.title),
        el('span', { class: 'dash-copy' }, disabled
          ? (reason.label || `Connect a source to add ${item.title}`)
          : item.desc),
        button(added ? 'On board' : (disabled ? 'Unavailable' : 'Add'), {
          className: added ? 'is-on' : '',
          disabled: disabled || undefined,
          onClick: disabled ? undefined : () => actions.toggleDraft(item.id),
        }));
    }))));
}

export function toolRow(widget) {
  const title = widgetMeta(widget.id).title;
  return el('div', { class: 'dash-tools' },
    el('button', {
      type: 'button',
      class: 'dash-handle',
      'aria-label': `Move ${title}. Use arrow keys.`,
    }, '⋮⋮'),
    el('span', { class: 'dash-tool-title' }, title),
    el('button', {
      type: 'button',
      class: 'dash-size',
      'aria-label': `Resize ${title}, now ${widget.size}`,
      'data-size': widget.id,
    }, widget.size),
    el('button', {
      type: 'button',
      class: 'dash-remove',
      'aria-label': `Remove ${title}`,
      'data-remove': widget.id,
    }, '×'));
}

/**
 * Pointer drag from the handle. Reorders when the pointer crosses another card.
 * @param {HTMLElement} board
 * @param {{ move: (id: string, delta: number) => void, editing: boolean }} actions
 */
export function bindDrag(board, actions) {
  if (!actions.editing || isPhone()) return;
  let drag = null;
  const ghost = el('div', { class: 'dash-ghost', 'aria-hidden': 'true' });
  function move(event) {
    if (!drag) return;
    ghost.style.transform = `translate(${event.clientX - drag.dx}px, ${event.clientY - drag.dy}px)`;
    const hit = document.elementFromPoint(event.clientX, event.clientY);
    const card = hit && hit.closest ? hit.closest('[data-wid]') : null;
    drag.over = card && card.dataset.wid !== drag.id ? card.dataset.wid : drag.over;
  }
  function end() {
    if (!drag) return;
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', end);
    ghost.remove();
    const target = drag.over;
    const id = drag.id;
    drag = null;
    if (!target) return;
    const cards = [...board.querySelectorAll('[data-wid]')];
    const from = cards.findIndex((node) => node.dataset.wid === id);
    const to = cards.findIndex((node) => node.dataset.wid === target);
    if (from >= 0 && to >= 0 && from !== to) actions.move(id, to - from);
  }
  board.querySelectorAll('.dash-handle').forEach((handle) => {
    handle.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      const card = handle.closest('[data-wid]');
      if (!card) return;
      event.preventDefault();
      const rect = card.getBoundingClientRect();
      drag = { id: card.dataset.wid, dx: event.clientX - rect.left, dy: event.clientY - rect.top, over: null };
      ghost.textContent = widgetMeta(drag.id).title;
      document.body.append(ghost);
      ghost.style.transform = `translate(${rect.left}px, ${rect.top}px)`;
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', end);
    });
    handle.addEventListener('keydown', (event) => {
      const card = handle.closest('[data-wid]');
      if (!card) return;
      let delta = 0;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') delta = -1;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') delta = 1;
      if (!delta) return;
      event.preventDefault();
      actions.move(card.dataset.wid, delta);
    });
  });
}
